import { useState } from "react"
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { Check, Link2 } from "lucide-react"
import { toast } from "sonner"
import type { CampaignDetail } from "@vienna/contracts"
import { CAMPAIGN_STATUS_LABELS } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Separator } from "@/components/ui/separator"
import { Header, Section, Status } from "@/components/audience/primitives"
import { providerLabel } from "@/components/campaigns/helpers"
import { BackLink, Footer, Steps } from "@/components/campaigns/primitives"
import { api, ApiError } from "@/lib/api"
import { formatDateTime } from "@/lib/format"
import { campaignEstimateQuery, campaignQuery } from "@/lib/queries"
import { useCan, useMe, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/campaigns/$campaignId/review")({ component: CampaignReview })

/** Screen 27. */
function CampaignReview() {
  const { workspace } = useWorkspaceContext()
  const me = useMe()
  const can = useCan()
  const { campaignId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const campaign = useSuspenseQuery(campaignQuery(workspace.slug, campaignId)).data
  const estimate = useQuery({ ...campaignEstimateQuery(workspace.slug, campaignId), enabled: Boolean(campaign.segmentId && campaign.connectionId) })
  const [confirm, setConfirm] = useState(false)
  const [ack, setAck] = useState(false)
  const params = { workspace: workspace.slug, campaignId }
  const scheduled = campaign.timing === "scheduled"
  const approve = useMutation({
    mutationFn: () => api<CampaignDetail>(`/w/${workspace.slug}/campaigns/${campaignId}/approve`, { method: "POST", body: JSON.stringify({ reviewed: true }) }),
    onSuccess: async (detail) => {
      queryClient.setQueryData(campaignQuery(workspace.slug, campaignId).queryKey, detail)
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "campaigns"] })
      toast.success(scheduled ? "Campaign scheduled" : "Campaign approved", { description: scheduled ? `Sends ${detail.scheduledAt ? formatDateTime(detail.scheduledAt) : "at the chosen time"}.` : "The audience is being prepared; delivery starts as soon as it is ready." })
      setConfirm(false)
      await navigate({ to: "/w/$workspace/campaigns/$campaignId/results", params })
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not approve the campaign"),
  })
  const checks: Array<[string, string, boolean]> = [
    ["Audience prepared", estimate.data ? `${estimate.data.eligible.toLocaleString()} eligible ${providerLabel(campaign.provider)} identities` : "Choose a segment and channel", Boolean(estimate.data && estimate.data.eligible > 0)],
    ["Permission and window checked", "Checked again before each send", true],
    ["Channel connected", campaign.connectionLabel ? `${campaign.connectionLabel} is available` : "No channel chosen", Boolean(campaign.connectionId)],
    ["Message validated", campaign.message.text.trim() ? "Personalization includes a fallback" : "Write a message first", Boolean(campaign.message.text.trim())],
  ]
  const ready = checks.every((c) => c[2]) && campaign.status === "draft"
  return (
    <div className="page-wrap au-root">
      <Header title={campaign.name} back={<BackLink to="/w/$workspace/campaigns" params={{ workspace: workspace.slug }}>Campaigns</BackLink>}>
        <Status>{CAMPAIGN_STATUS_LABELS[campaign.status]}</Status>
      </Header>
      <Steps current="review" workspace={workspace.slug} campaignId={campaignId} />
      <div className="au-two-column">
        <Section title="Review your campaign" description="Check the details before confirming.">
          <div className="mb-3 flex justify-between">
            <h3 className="font-medium">Audience</h3>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/w/$workspace/campaigns/$campaignId/audience" params={params}>
                Edit
              </Link>
            </Button>
          </div>
          <p>{campaign.segmentName ?? "No segment chosen"}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {providerLabel(campaign.provider)}
            {campaign.connectionLabel ? ` · ${campaign.connectionLabel}` : ""}
          </p>
          <div className="my-5 flex flex-wrap gap-2">
            <Badge variant="secondary">{(estimate.data?.selected ?? 0).toLocaleString()} selected</Badge>
            <Badge variant="secondary">{(estimate.data?.eligible ?? 0).toLocaleString()} eligible</Badge>
            <Badge variant="outline">{(estimate.data?.excluded ?? 0).toLocaleString()} excluded</Badge>
          </div>
          <Separator />
          <div className="my-4 flex justify-between">
            <h3 className="font-medium">Message</h3>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/w/$workspace/campaigns/$campaignId/message" params={params}>
                Edit
              </Link>
            </Button>
          </div>
          <p className="text-sm leading-6 whitespace-pre-wrap">{campaign.message.text || "No message yet."}</p>
          {campaign.message.button && (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Badge variant="outline">
                <Link2 className="size-3" />
                {campaign.message.button.label}
              </Badge>
              <span className="text-xs text-muted-foreground">{campaign.message.button.url}</span>
            </div>
          )}
          <Separator className="my-5" />
          <div className="flex justify-between">
            <div>
              <h3 className="font-medium">Timing</h3>
              <p className="mt-2 text-sm text-muted-foreground">{scheduled && campaign.scheduledAt ? `${formatDateTime(campaign.scheduledAt)} · ${campaign.timezone}` : "Send immediately after confirmation"}</p>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/w/$workspace/campaigns/$campaignId/schedule" params={params}>
                Edit
              </Link>
            </Button>
          </div>
        </Section>
        <Section title={ready ? "Ready for review" : "Not ready yet"} description={ready ? "Everything is in place." : "Complete the checks below."}>
          <div className="space-y-5">
            {checks.map(([title, detail, ok]) => (
              <div className="flex gap-3" key={title}>
                <span className={`au-check ${ok ? "" : "opacity-40"}`}>
                  <Check className="size-3" />
                </span>
                <div>
                  <p className="text-sm font-medium">{title}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
                </div>
              </div>
            ))}
          </div>
          <Separator className="my-5" />
          <p className="text-xs text-muted-foreground">Estimated delivery attempts</p>
          <p className="my-2 text-3xl font-semibold tracking-tight">Up to {(estimate.data?.eligible ?? 0).toLocaleString()}</p>
          <p className="text-xs leading-5 text-muted-foreground">People whose eligibility changes are skipped with a reason. Prepared by {me.name}.</p>
        </Section>
      </div>
      <Footer
        back={<BackLink to="/w/$workspace/campaigns/$campaignId/schedule" params={params}>Back</BackLink>}
        label={campaign.status !== "draft" ? "View results" : scheduled ? "Schedule campaign" : "Send campaign"}
        disabled={campaign.status === "draft" && (!ready || !can("publish"))}
        note={can("publish") ? "Approval is recorded in the audit log" : "Sending needs the marketing or admin role"}
        onNext={() => (campaign.status !== "draft" ? void navigate({ to: "/w/$workspace/campaigns/$campaignId/results", params }) : setConfirm(true))}
      />
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{scheduled ? "Schedule this campaign?" : "Send this campaign?"}</DialogTitle>
            <DialogDescription>
              {scheduled && campaign.scheduledAt ? `Scheduled for ${formatDateTime(campaign.scheduledAt)} (${campaign.timezone}).` : `Up to ${(estimate.data?.eligible ?? 0).toLocaleString()} ${providerLabel(campaign.provider)} recipients are currently eligible.`} The audience is frozen now and every recipient is checked again before their message is sent.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-3 text-sm">
            <Checkbox checked={ack} onCheckedChange={(value) => setAck(value === true)} className="mt-0.5" />
            I’ve reviewed the audience, message and timing.
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)}>
              Keep editing
            </Button>
            <Button disabled={!ack || approve.isPending} onClick={() => approve.mutate()}>
              {scheduled ? "Confirm schedule" : "Send campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
